import { Injectable } from '@angular/core';
import { NgxSpinnerService } from "ngx-spinner";
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import {Observable} from "rxjs";
import { finalize, tap } from 'rxjs/operators';
import { StorageService } from './shared/storage.service';
import { ApiRequestService } from './shared/api-request.service';
import { UtilsService } from './shared/utils.service';
import { AuthService } from './shared/auth.service';

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private spinner: NgxSpinnerService,
    private storage: StorageService,
    private api: ApiRequestService,
    private utils: UtilsService,
    private authService: AuthService
  ) { }

  /**
   * Intercepts an HTTP request and handles it while showing a loader.
   *
   * @param req The HTTP request to be intercepted
   * @param next The HTTP handler for the request
   * @return An Observable of the HTTP event with loader handling
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip spinner for background requests (e.g. autocomplete search)
    // to avoid DOM manipulation that interrupts mobile keyboard input.
    if (req.headers.has('X-Skip-Spinner')) {
      const cleanReq = req.clone({
        headers: req.headers.delete('X-Skip-Spinner')
      });
      return next.handle(cleanReq);
    }

    // Show loader when HTTP request is made
    this.spinner.show();

    // Intercept and handle the HTTP request.
    return next.handle(req).pipe(
      tap((event: HttpEvent<any>): void => {
        if ( event instanceof HttpResponse ) {
          const refreshedToken: string = event.headers.get('X-Refreshed-Token'); // This will only be present if the previous token was stale.
          if ( refreshedToken ) {
            // Store the refreshed token in local storage.
            this.storage.set('AuthToken', refreshedToken);
            // Validate the API token. It will also, in turn, update the user's permissions in the frontend.
            // Flagged as a background request: the user did not trigger it, so a failure to reach the
            // server must not open a modal.
            this.api.makeRequest('post', 'v2/api-token-validation', {
              api_token: refreshedToken
            }, {}, {}, false, true)
              .then((response: any): void => {
                this.authService.updateAccountAuthDetails(response);
              })
              .catch((errors): void => {
                // Only an answer from the API that the token is invalid ends the session. A transport
                // failure, a rate limit or a server error says nothing about the token, which was just
                // refreshed and stored, so the user stays signed in. The permissions then catch up on
                // the next page load or tab focus validation (authDetailsDiffer in app.component.ts).
                if ( errors?.status === 401 || errors?.status === 403 ) {
                  this.utils.handleAPIErrors(errors);
                  // Sign the user out if the API token is invalid.
                  this.authService.signOut('Please sign in again to continue using the application.');
                }
              });
          }
        }
      }),
      finalize((): void => {
        // Hide loader when HTTP request is complete (success or error)
        this.spinner.hide();
      })
    );
  }
}
